/**
 * 検索履歴機能
 * - localStorageに検索ワード / タグクリックを保存
 * - 検索窓フォーカス時にpredictive-searchのドロップダウン上部に履歴一覧を表示
 * - PASS: defensive try/catch around localStorage to support private-mode
 */

(function () {
  'use strict';
  // build: pakari-search-history v7 (260517: モバイルpresearch top JS強制計算)

  const STORAGE_KEY = 'pakari_search_history';
  const MAX_LABEL_LENGTH = 50;
  // 商品ページの販売元タグはテーマエディタで tag_link_enabled が ON のとき
  // `.product-tag-link` で出力されるが、OFF時は `.product__text` 内に
  // `<a href="/collections/all/...">` として出るケースも捕捉できるよう拡張
  const TAG_SELECTORS = [
    '.product-tag-link',
    '.product-work-tag',
    '.product-tag-links a',
    '.product__text a[href*="/collections/all/"]'
  ].join(', ');

  const storage = {
    read() {
      try {
        const raw = localStorage.getItem(STORAGE_KEY);
        if (!raw) return [];
        const parsed = JSON.parse(raw);
        return Array.isArray(parsed) ? parsed : [];
      } catch (e) {
        return [];
      }
    },
    write(items) {
      try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
      } catch (e) {
        /* private mode などで保存不可。機能のみ無効化 */
      }
    },
    clear() {
      try {
        localStorage.removeItem(STORAGE_KEY);
      } catch (e) {
        /* noop */
      }
    },
  };

  function normalizeLabel(rawLabel) {
    if (typeof rawLabel !== 'string') return '';
    let label = rawLabel.trim();
    if (!label) return '';
    if (label.length > MAX_LABEL_LENGTH) {
      label = label.substring(0, MAX_LABEL_LENGTH);
    }
    return label;
  }

  function isExpired(item, retentionDays) {
    if (retentionDays === 'unlimited') return false;
    const days = parseInt(retentionDays, 10);
    if (!days || !item.ts) return false;
    const ageMs = Date.now() - item.ts;
    return ageMs > days * 24 * 60 * 60 * 1000;
  }

  // 履歴アイテムの種別（バッジ表示の出し分けに使用）
  const ENTRY_TYPES = ['search', 'tag', 'collection'];

  /**
   * 履歴に追加。同 label の既存アイテムは除外して先頭に追加。
   * @param {Object} entry - { type: 'search'|'tag'|'collection', label, url }
   */
  function addEntry(entry) {
    const label = normalizeLabel(entry.label);
    if (!label) return;
    if (!entry.url) return;

    const items = storage.read();
    const filtered = items.filter((it) => it.label !== label);
    filtered.unshift({
      type: ENTRY_TYPES.indexOf(entry.type) !== -1 ? entry.type : 'search',
      label: label,
      url: entry.url,
      ts: Date.now(),
    });
    storage.write(filtered);

    // 既存表示中の<search-history>すべてを再描画
    document.querySelectorAll('search-history').forEach((el) => {
      if (typeof el.render === 'function') el.render();
    });
  }

  function removeEntry(label) {
    const items = storage.read();
    const filtered = items.filter((it) => it.label !== label);
    storage.write(filtered);
  }

  function clearAll() {
    storage.clear();
  }

  // 外部公開（predictive-search.js から呼ばれる）
  window.PakariSearchHistory = {
    addEntry: addEntry,
    removeEntry: removeEntry,
    clearAll: clearAll,
    read: storage.read,
  };

  /**
   * タグクリックのイベント委譲。
   * 商品詳細ページのタグ・作品名タグ・セール期間タグを一括捕捉。
   * sale-period-tag.liquid も `.product-tag-link` クラスを共有しているため、
   * 同セレクタで包含可能。
   */
  document.addEventListener('click', function (event) {
    const target = event.target.closest(TAG_SELECTORS);
    if (!target) return;
    const href = target.getAttribute('href');
    if (!href) return;
    const label = (target.textContent || '').trim();
    if (!label) return;
    addEntry({ type: 'tag', label: label, url: href });
  }, true); // capture phase でナビゲーション前に確実に保存

  /**
   * predictive-search のサジェスト商品リンククリックを捕捉。
   * click より早く発火する mousedown capture phase で履歴保存することで、
   * ブラウザのナビゲーション開始前に localStorage へ確実に書き込む。
   * pointerdown も併用（タッチデバイス対応）。
   *
   * セレクタは広めに：a / li / 内部 span を拾えるよう closest を多段で。
   * input.value が空のケース（既に遷移開始等）は item.textContent を fallback。
   */
  function captureSuggestClick(event) {
    const target = event.target;
    if (!target || !target.closest) return;
    // サジェスト要素関連を広く捕捉
    const item = target.closest(
      '.predictive-search__item, .predictive-search__list-item, [data-predictive-search] a, [data-predictive-search] li'
    );
    if (!item) return;
    if (!item.closest('predictive-search')) return;

    // 「クリックした商品のタイトル」を履歴に保存する（入力中ワードではなく）
    const textEl =
      item.querySelector('.predictive-search__item-heading, .predictive-search__item-title-truncate, .noline_animate') ||
      item;
    const label = (textEl.textContent || '').trim();
    if (!label) return;

    addEntry({
      type: 'search',
      label: label,
      url: '/search?q=' + encodeURIComponent(label) + '&type=product',
    });
  }
  document.addEventListener('mousedown', captureSuggestClick, true);
  document.addEventListener('pointerdown', captureSuggestClick, true);
  // フォールバック（mousedownが何らかの理由で発火しない場合）
  document.addEventListener('click', captureSuggestClick, true);

  /**
   * 「コレクション検索」の履歴保存。
   * コレクションへの導線はヘッダーナビ等からの直接遷移が主で（デフォルト検索パネルの
   * コレクションタブは collection_list 未設定時は空）、クリック捕捉では取りこぼすため、
   * コレクションページ表示時に当該コレクションを履歴へ記録する（type: 'collection'）。
   * 対象判定は main-collection-product-grid 側が出力する [data-collection-history] マーカー。
   * タグ絞り込み中（current_tags あり＝タグ検索）や検索結果ページは Liquid 側で出力しないため記録されない。
   */
  function recordCollectionPage() {
    const el = document.querySelector('[data-collection-history]');
    if (!el) return;
    const label = (el.getAttribute('data-collection-label') || '').trim();
    const url = el.getAttribute('data-collection-url');
    if (!label || !url) return;
    addEntry({ type: 'collection', label: label, url: url });
  }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', recordCollectionPage);
  } else {
    recordCollectionPage();
  }

  /**
   * 260517 改修④：モバイル ヘッダー検索バーの .pakari-mobile-presearch の top を
   * 実機の .pakari-mobile-search-inner.bottom にぴったり合わせる。
   * CSS の `top: 100%` 等が他のスタイルに上書きされる環境でも確実に隙間をゼロにするため、
   * JS で要素の実位置から計算して style.top を直接設定する。
   */
  function adjustMobilePresearchTop() {
    if (window.matchMedia && !window.matchMedia('(max-width: 991px)').matches) return;
    document.querySelectorAll('.pakari-mobile-search').forEach((bar) => {
      const inner = bar.querySelector('.pakari-mobile-search-inner');
      const presearch = bar.querySelector('.pakari-mobile-presearch');
      if (!inner || !presearch) return;
      const barRect = bar.getBoundingClientRect();
      const innerRect = inner.getBoundingClientRect();
      const offsetTop = innerRect.bottom - barRect.top;
      presearch.style.setProperty('top', offsetTop + 'px', 'important');
      presearch.style.setProperty('margin-top', '0', 'important');
      presearch.style.setProperty('padding-top', '0', 'important');
    });
  }
  window.addEventListener('DOMContentLoaded', adjustMobilePresearchTop);
  window.addEventListener('load', adjustMobilePresearchTop);
  window.addEventListener('resize', adjustMobilePresearchTop);
  // predictive-search の open/loading 状態変更時にも再計算
  // 260612-2：検索履歴/サジェスト表示時の全画面オーバーレイ。
  // sticky-header等の transform 祖先内では position:fixed がclipされるため body 直下に置く。
  function updateSearchOverlay() {
    // モーダル版(.search-modal__content内)は既存の .modal-overlay があるため除外
    var openPs = Array.prototype.slice
      .call(document.querySelectorAll('predictive-search[open]'))
      .filter(function (ps) {
        return !ps.closest('.search-modal__content');
      });
    var active = openPs.length > 0;
    // 既存のオーバーレイは毎回除去し、有効時のみ新規生成して body 末尾に置く
    document.querySelectorAll('.pakari-search-overlay').forEach(function (el) {
      el.parentNode && el.parentNode.removeChild(el);
    });
    if (active) {
      var ov = document.createElement('div');
      ov.className = 'pakari-search-overlay';
      ov.style.cssText =
        'position:fixed;top:0;left:0;width:100%;height:100%;background:rgba(0,0,0,0.5);z-index:9999000;pointer-events:none;';
      document.body.appendChild(ov);
    }
    // ヘッダーセクションをオーバーレイより前面に上げて検索UIを見せる
    document.body.classList.toggle('pakari-search-open', active);
  }

  if (window.MutationObserver) {
    document.addEventListener('DOMContentLoaded', function () {
      const obs = new MutationObserver(function () {
        adjustMobilePresearchTop();
        updateSearchOverlay();
      });
      document.querySelectorAll('predictive-search').forEach((el) => {
        obs.observe(el, { attributes: true, attributeFilter: ['open', 'loading'] });
      });
      // フォーカス時にも保険として再計算
      document.querySelectorAll('.pakari-mobile-search-input').forEach((inp) => {
        inp.addEventListener('focus', adjustMobilePresearchTop);
        inp.addEventListener('input', adjustMobilePresearchTop);
      });
      updateSearchOverlay();
    });
  }

  class SearchHistory extends HTMLElement {
    constructor() {
      super();
      this.list = null;
      this.clearAllBtn = null;
      this.predictiveSearch = null;
    }

    connectedCallback() {
      this.list = this.querySelector('[data-history-list]');
      this.clearAllBtn = this.querySelector('[data-clear-all]');
      this.predictiveSearch = this.closest('predictive-search');

      this.maxItems = parseInt(this.dataset.maxItems, 10) || 10;
      this.retention = this.dataset.retention || '30';
      this.tagBadgeText = this.dataset.tagBadgeText || 'TAG';
      this.catBadgeText = this.dataset.catBadgeText || 'CATEGORY';
      this.showCategory = this.dataset.showCategory !== 'false'; // 260612要件：既定 true

      // ×ボタン/すべて消去クリック時にフォーカスが入力欄から外れて
      // onFocusOut → close されないよう、mousedown を抑止してフォーカス保持
      this.addEventListener('mousedown', (e) => {
        if (e.target.closest('[data-delete], [data-clear-all]')) {
          e.preventDefault();
        }
      });

      this.clearAllBtn?.addEventListener('click', this.onClearAll.bind(this));
      this.list?.addEventListener('click', this.onListClick.bind(this));

      // 初期状態：hidden を維持。中身だけ描画し、表示制御は show()/hide() に委ねる
      this.renderList();
    }

    /**
     * 表示用に保存期間内かつ最大表示数までに絞り込む。
     * 期限切れアイテムは保存からも除外（読込時クリーンアップ）。
     */
    getVisibleItems() {
      const all = storage.read();
      const alive = all.filter((it) => !isExpired(it, this.retention));
      if (alive.length !== all.length) {
        storage.write(alive);
      }
      let visible = alive;
      if (!this.showCategory) {
        // 260612要件：カテゴリータグ履歴OFF時は collection 種別を表示から除外（保存は維持）
        visible = visible.filter((it) => it.type !== 'collection');
      }
      return visible.slice(0, this.maxItems);
    }

    /**
     * リスト中身のみ描画。表示/非表示は変更しない。
     * 履歴0件のときも hidden 状態を維持（show() 側で hasItems() を確認する）
     */
    renderList() {
      if (!this.list) return;
      const items = this.getVisibleItems();
      this.list.innerHTML = items.map((it, idx) => this.itemHtml(it, idx)).join('');
    }

    /**
     * リスト中身を描画し、現在表示中なら表示状態を更新。
     * 現在非表示なら表示状態は変えない（フォーカス前は強制表示しない）。
     */
    render() {
      this.renderList();
      // 既に表示中のときだけ：履歴が0件になったら閉じる
      if (!this.classList.contains('hidden') && !this.hasItems()) {
        this.classList.add('hidden');
      }
    }

    itemHtml(item, index) {
      const safeLabel = this.escapeHtml(item.label);
      const safeUrl = this.escapeAttr(item.url);
      // バッジ：tag→「TAG」、collection→「CATEGORY」（共に .search-history__tag-badge で同一デザイン）
      let badge = '';
      if (item.type === 'tag') {
        badge = `<span class="search-history__tag-badge">${this.escapeHtml(this.tagBadgeText)}</span>`;
      } else if (item.type === 'collection') {
        badge = `<span class="search-history__tag-badge search-history__cat-badge">${this.escapeHtml(this.catBadgeText)}</span>`;
      }
      const itemId = 'search-history-option-' + (this.dataset.context || 'main') + '-' + index;
      return (
        '<li class="search-history__item" id="' + itemId + '" role="option" aria-selected="false" data-label="' + this.escapeAttr(item.label) + '">' +
        '<a class="search-history__link" href="' + safeUrl + '">' +
        badge +
        '<span class="search-history__label">' + safeLabel + '</span>' +
        '</a>' +
        '<button type="button" class="search-history__delete" data-delete aria-label="削除">' +
        '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="14" height="14" aria-hidden="true" focusable="false"><path d="M6 6 L18 18 M18 6 L6 18" stroke="currentColor" stroke-width="2" stroke-linecap="round" fill="none"/></svg>' +
        '</button>' +
        '</li>'
      );
    }

    escapeHtml(str) {
      return String(str)
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;');
    }

    escapeAttr(str) {
      return this.escapeHtml(str);
    }

    onClearAll() {
      clearAll();
      this.render();
      // 全インスタンスを同期再描画
      document.querySelectorAll('search-history').forEach((el) => {
        if (el !== this && typeof el.render === 'function') el.render();
      });
    }

    onListClick(event) {
      const deleteBtn = event.target.closest('[data-delete]');
      if (deleteBtn) {
        event.preventDefault();
        event.stopPropagation();
        const li = deleteBtn.closest('.search-history__item');
        const label = li?.dataset.label;
        if (label) {
          removeEntry(label);
          this.render();
          document.querySelectorAll('search-history').forEach((el) => {
            if (el !== this && typeof el.render === 'function') el.render();
          });
        }
        return;
      }
      // 履歴アイテムの <a> クリック：選択した項目をlocalStorage上で先頭に移動
      // （次回表示時に最上部に来るようts更新つきで再投入）。デフォルト遷移はそのまま実行。
      const link = event.target.closest('.search-history__link');
      if (link) {
        const li = link.closest('.search-history__item');
        const label = li?.dataset.label;
        if (label) {
          const items = storage.read();
          const item = items.find((it) => it.label === label);
          if (item) {
            addEntry({ type: item.type, label: item.label, url: item.url });
          }
        }
      }
    }

    /** 履歴アイテムが1件でも存在するか */
    hasItems() {
      return this.getVisibleItems().length > 0;
    }

    show() {
      if (this.hasItems()) {
        this.classList.remove('hidden');
      }
    }

    hide() {
      this.classList.add('hidden');
      // 隠す際に aria-selected をクリア（onFormSubmit の querySelector で
      // 古い状態を拾ってフォーム送信が preventDefault されてしまう問題の防止）
      this.querySelectorAll('[aria-selected="true"]').forEach((el) => {
        el.setAttribute('aria-selected', 'false');
      });
    }
  }

  customElements.define('search-history', SearchHistory);
})();
